import React, {useState, useCallback, useEffect, useMemo} from 'react';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import FloatingMenuButton, {
    FloatingMenuButtonIcon,
} from 'components/customer/RoomPlanner/components/common/FloatingMenuButton';
import ProductEvent from 'components/customer/RoomPlanner/lib/useProductEvents';
import {
    getCabinetDoorGap,
    isDoorOrDrawerOpen,
    setDoorOrDrawerOpen,
    hasDoors,
    CabinetGroup,
} from 'components/customer/RoomPlanner/helpers/doorDetection';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {getDoorFrontsMode} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {useAppSelector} from 'store/customer';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';

const DoorToggle = () => {
    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const {selectedCabinetGroup, setDoorToggleHandler} =
        useRoomPlannerContext();
    const doorFrontsMode = useAppSelector(getDoorFrontsMode);
    const frontsHidden = doorFrontsMode === 'hide';
    const [isOpen, setIsOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    // Check if cabinet has doors using the centralized function
    const cabinetHasDoors = selectedCabinetGroup
        ? hasDoors(selectedCabinetGroup as CabinetGroup)
        : false;

    // Match the selected cabinet. Open and Shut on the top bar update every door.
    useEffect(() => {
        if (doorFrontsMode === 'open' || doorFrontsMode === 'shut') {
            setIsOpen(doorFrontsMode === 'open');
            return;
        }

        setIsOpen(Boolean(selectedCabinetGroup?.userData?.isDoorOrDrawerOpen));
    }, [selectedCabinetGroup, doorFrontsMode]);

    const toggleDoor = useCallback(async () => {
        if (!selectedCabinetGroup || isAnimating || frontsHidden) {
            return;
        }

        // Don't allow door toggle for cabinets without doors
        if (!cabinetHasDoors) {
            return;
        }

        const doorGap = getCabinetDoorGap(selectedCabinetGroup as CabinetGroup);
        const currentlyOpen = isDoorOrDrawerOpen(
            selectedCabinetGroup as CabinetGroup
        );
        const nextOpenState = !currentlyOpen;

        setIsAnimating(true);
        setIsOpen(nextOpenState);
        setDoorOrDrawerOpen(
            selectedCabinetGroup as CabinetGroup,
            nextOpenState
        );

        // Type assertion is safe here because activeGroup is confirmed to be a cabinet group
        // and CabinetGroup extends THREE.Group which is compatible with CabinetMesh
        try {
            await ProductEvent.triggerEventsAsync(
                selectedCabinetGroup,
                'toggleDoor',
                {
                    doorGap,
                    isDoorOrDrawerOpen: nextOpenState,
                }
            );
        } finally {
            setIsAnimating(false);
        }
    }, [selectedCabinetGroup, cabinetHasDoors, isAnimating, frontsHidden]);

    const label = useMemo(() => {
        if (cabinetHasDoors) {
            if (isOpen) {
                return 'Close';
            } else {
                return 'Open';
            }
        }
    }, [isOpen, cabinetHasDoors]);

    const handleToggleDoorClick = useCallback(() => {
        void toggleDoor();
    }, [toggleDoor]);

    // Register the door toggle handler with the context for keyboard shortcut
    useEffect(() => {
        if (setDoorToggleHandler) {
            setDoorToggleHandler(handleToggleDoorClick);
        }
        return () => setDoorToggleHandler?.(null);
    }, [handleToggleDoorClick, setDoorToggleHandler]);

    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <strong>
                    {!cabinetHasDoors
                        ? 'No doors available'
                        : frontsHidden
                          ? 'Fronts are hidden'
                          : isOpen
                            ? 'Close door (O)'
                            : 'Open Door (O)'}
                </strong>
            }>
            <FloatingMenuButton
                onClick={handleToggleDoorClick}
                disabled={isAnimating || !cabinetHasDoors || frontsHidden}
                style={{
                    opacity:
                        cabinetHasDoors && !isAnimating && !frontsHidden
                            ? 1
                            : 0.5,
                    cursor:
                        cabinetHasDoors && !isAnimating && !frontsHidden
                            ? 'pointer'
                            : 'not-allowed',
                }}>
                <FloatingMenuButtonIcon
                    className="icon_button"
                    color={
                        isMobileLandscape || isPortraitDevices
                            ? undefined
                            : cabinetHasDoors && !frontsHidden
                              ? '#b2bec3'
                              : '#666'
                    }
                    iconName={
                        isOpen
                            ? isMobileLandscape || isPortraitDevices
                                ? 'preview/Drawers-Closed.svg'
                                : 'preview/Drawers-Closed-Gray.svg'
                            : isMobileLandscape || isPortraitDevices
                              ? 'preview/Drawers-Open.svg'
                              : 'preview/Drawers-Open-Gray.svg'
                    }
                />
                <label>{label}</label>
            </FloatingMenuButton>
        </OverlayTrigger>
    );
};

export default DoorToggle;
